import {
  type Auftrag,
  type AuftragsStatus,
  type AuftragsTyp,
  STILLSTAND_NACH_MS,
  stehtStill,
} from '@awf/shared';
import { AUFTRAGSTYPEN, type Schritt, TYP_REIHENFOLGE } from '../auftragstypen.js';

export const STATUS_TEXT: Record<AuftragsStatus, string> = {
  angelegt: 'Angelegt',
  recherche: 'Recherche',
  entwurf: 'Entwurf',
  wartet_auf_freigabe: 'Wartet auf Freigabe',
  wartet_auf_entscheidung: 'Wartet auf Entscheidung',
  in_ueberarbeitung: 'In Überarbeitung',
  finalisierung: 'Finalisierung',
  fertig: 'Fertig',
  fehlgeschlagen: 'Fehlgeschlagen',
  abgebrochen: 'Abgebrochen',
};

export const TYP_TEXT = Object.fromEntries(
  TYP_REIHENFOLGE.map((typ) => [typ, AUFTRAGSTYPEN[typ].text]),
) as Record<AuftragsTyp, string>;

export type StatusGruppe = 'wartet' | 'laeuft' | 'fertig' | 'fehler';

export const GRUPPEN_TEXT: Record<StatusGruppe, string> = {
  wartet: 'Wartet auf Freigabe',
  laeuft: 'Läuft',
  fertig: 'Fertig',
  fehler: 'Fehler',
};

/** Die Status, in denen ein Mensch am Zug ist. Liste, Seitenleiste und Haengeranzeige teilen sie. */
export const WARTE_STATUS: readonly AuftragsStatus[] = [
  'wartet_auf_freigabe',
  'wartet_auf_entscheidung',
];

/**
 * Ein am PDP-Tor verworfener Auftrag: technisch `abgebrochen`, fachlich eine bewusste Entscheidung.
 * Die Detailseite zeigt ihn neutral statt als Stoerung (Story "PDP-Kleinkram").
 */
export function istVerworfen(auftrag: Pick<Auftrag, 'status' | 'pdpEntscheidung'>): boolean {
  return auftrag.status === 'abgebrochen' && auftrag.pdpEntscheidung?.aktion === 'verwerfen';
}

export function gruppeVon(status: AuftragsStatus): StatusGruppe {
  if (WARTE_STATUS.includes(status)) return 'wartet';
  if (status === 'fertig') return 'fertig';
  if (status === 'fehlgeschlagen' || status === 'abgebrochen') return 'fehler';
  return 'laeuft';
}

/**
 * Der Vorwaerts-Ablauf je Kette steht im Typ-Register (`schritte`). `in_ueberarbeitung` steht
 * dort bewusst NICHT: es ist ein Ruecksprung auf den Entwurf, kein Schritt danach.
 */
const NORMALISIERUNG: Partial<Record<AuftragsStatus, AuftragsStatus>> = {
  in_ueberarbeitung: 'entwurf',
};

export function normalisiere(status: AuftragsStatus): AuftragsStatus {
  return NORMALISIERUNG[status] ?? status;
}

export function fortschritt(
  typ: AuftragsTyp,
  status: AuftragsStatus,
): { schritt: number; von: number } {
  // Breit annotiert: das Register behaelt die Literale, indexOf mit jedem Status muss gehen.
  const ablauf: AuftragsStatus[] = AUFTRAGSTYPEN[typ].schritte.map((s) => s.status);
  const index = ablauf.indexOf(normalisiere(status));
  return { schritt: index === -1 ? 0 : index + 1, von: ablauf.length };
}

export function aktuellerSchritt(typ: AuftragsTyp, status: AuftragsStatus): Schritt | undefined {
  const gesucht = normalisiere(status);
  return AUFTRAGSTYPEN[typ].schritte.find((s) => s.status === gesucht);
}

const GRUPPEN_RANG: Record<StatusGruppe, number> = {
  wartet: 0,
  laeuft: 1,
  fertig: 2,
  fehler: 3,
};

export function sortiereAuftraege<T extends Pick<Auftrag, 'status' | 'angelegtAm'>>(
  auftraege: readonly T[],
): T[] {
  return [...auftraege].sort((a, b) => {
    const rang = GRUPPEN_RANG[gruppeVon(a.status)] - GRUPPEN_RANG[gruppeVon(b.status)];
    if (rang !== 0) return rang;
    return b.angelegtAm.localeCompare(a.angelegtAm);
  });
}

/**
 * Die fachliche Antwort auf "kein Dauer-Polling ohne Ende": Statt endlos zu warten, sagt die
 * Oberflaeche irgendwann, dass etwas nicht stimmt. Dieselbe Regel wie fuer das Loeschen
 * (`stehtStill`, packages/shared), je Auftragstyp mit eigener Grenze.
 */
export const HAENGT_NACH_MS = STILLSTAND_NACH_MS;

export function istHaengend(
  auftrag: Pick<Auftrag, 'typ' | 'status' | 'aktualisiertAm'>,
  jetzt: Date,
): boolean {
  return stehtStill(auftrag, jetzt.getTime());
}
